import React, {useState, useRef} from 'react';
import {ComposedModal, ModalHeader, ModalBody, ModalFooter, Button, Tag} from '@carbon/react';
import {WarningFilled, CloudUpload, TrashCan, Download, InformationFilled, Launch, Error as ErrorIcon} from '@carbon/icons-react';
import {ReactComponent as XlsxIcon} from '../../xlsx-icon.svg';
import {REQUIREMENTS, ACCEPTED_MIME, formatBytes, getFileTypeName} from '../../upload-files-modal';
import '../../upload-files-modal.scss';

export interface ResubmitFileModalProps {
    isOpen: boolean;
    originalFileName: string;
    uploadedAt: string;
    fileSize: string;
    onClose: () => void;
    onResubmit: (file: File) => void;
}

export const ResubmitFileModal: React.FC<ResubmitFileModalProps> = ({
    isOpen,
    originalFileName,
    uploadedAt,
    fileSize,
    onClose,
    onResubmit,
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isDragInvalid, setIsDragInvalid] = useState(false);
    const [invalidTypeError, setInvalidTypeError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);

    const nameMatches = selectedFile?.name === originalFileName;
    const showMismatch = selectedFile !== null && !nameMatches;

    const handleClose = () => {
        setSelectedFile(null);
        setIsDragging(false);
        setIsDragInvalid(false);
        setInvalidTypeError('');
        onClose();
    };

    const handleSubmit = () => {
        if (!selectedFile || !nameMatches) return;
        onResubmit(selectedFile);
    };

    const handleFileSelect = (file: File) => {
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext === 'csv' || ext === 'xlsx') {
            setInvalidTypeError('');
            setSelectedFile(file);
        } else {
            setInvalidTypeError('Invalid file type. Only .csv and .xlsx files are accepted.');
            setSelectedFile(null);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
        e.target.value = '';
    };

    const hasInvalidDragItems = (items: DataTransferItemList): boolean =>
        Array.from(items).some(item => item.kind === 'file' && !!item.type && !ACCEPTED_MIME.has(item.type));

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        if (dropZoneRef.current?.contains(e.relatedTarget as Node)) return;
        const invalid = hasInvalidDragItems(e.dataTransfer.items);
        setIsDragInvalid(invalid);
        setIsDragging(!invalid);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        if (dropZoneRef.current?.contains(e.relatedTarget as Node)) return;
        setIsDragging(false);
        setIsDragInvalid(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        setIsDragInvalid(false);
        if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
    };

    return (
        <ComposedModal
            open={isOpen}
            onClose={handleClose}
            size="md"
            className="upload-files-modal-container"
        >
            <ModalHeader title="Resubmit File" />
            <ModalBody hasScrollingContent>
                <div className="upload-files-modal">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx"
                        className="upload-hidden-input"
                        onChange={handleInputChange}
                    />

                    <p className="upload-modal-subtitle">
                        Upload an updated version of this file to fix issues and restart processing.
                    </p>

                    <p style={{marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#161616'}}>
                        Original file
                    </p>
                    <ul className="upload-file-list" style={{marginBottom: '1.5rem'}}>
                        <li className="upload-file-item">
                            <XlsxIcon className="upload-file-type-icon" />
                            <div className="upload-file-item__info">
                                <span className="upload-file-item__name" title={originalFileName}>{originalFileName}</span>
                                <span className="upload-file-item__meta">Uploaded {uploadedAt} &bull; {fileSize}</span>
                            </div>
                            <Tag type="red" size="sm">Needs Review</Tag>
                        </li>
                    </ul>

                    <p style={{marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#161616'}}>
                        New file
                    </p>

                    {!selectedFile ? (
                        <>
                            <div
                                ref={dropZoneRef}
                                className={`upload-drop-zone${isDragging ? ' upload-drop-zone--dragging' : ''}${isDragInvalid ? ' upload-drop-zone--invalid' : ''}`}
                                onDragEnter={handleDragEnter}
                                onDragLeave={handleDragLeave}
                                onDragOver={e => e.preventDefault()}
                                onDrop={handleDrop}
                                aria-label="Drop CSV or XLSX file here"
                            >
                                {isDragInvalid ? (
                                    <>
                                        <ErrorIcon size={48} className="upload-drop-zone__invalid-icon" />
                                        <p className="upload-drop-zone__invalid-heading">Unsupported file type</p>
                                        <p className="upload-drop-zone__invalid-body">Please upload CSV or XLSX files only.</p>
                                    </>
                                ) : (
                                    <>
                                        <CloudUpload size={48} className="upload-drop-zone__icon" />
                                        <p className="upload-drop-zone__heading">
                                            {isDragging ? 'Drop file here' : 'Drag and drop a file here'}
                                        </p>
                                        <p className="upload-drop-zone__or">or</p>
                                        <button
                                            type="button"
                                            className="upload-drop-zone__browse-btn"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Browse Files
                                        </button>
                                        <p className="upload-drop-zone__hint">Accepted file types: .csv, .xlsx</p>
                                    </>
                                )}
                            </div>
                            {invalidTypeError && (
                                <p style={{margin: '0 0 0.5rem', fontSize: '0.75rem', color: '#da1e28'}}>{invalidTypeError}</p>
                            )}
                            <p style={{margin: '0 0 1.5rem', fontSize: '0.75rem', color: '#525252'}}>
                                The new file must use the same file name as the original file.<br />
                                Rename your file if needed before uploading.
                            </p>
                        </>
                    ) : (
                        <>
                            <ul className="upload-file-list" style={{marginBottom: '0.5rem'}}>
                                <li className={`upload-file-item${showMismatch ? ' upload-file-item--invalid' : ''}`}>
                                    <XlsxIcon className="upload-file-type-icon" />
                                    <div className="upload-file-item__info">
                                        <span className="upload-file-item__name" title={selectedFile.name}>{selectedFile.name}</span>
                                        {showMismatch ? (
                                            <span className="upload-file-item__error-msg">
                                                <WarningFilled size={12} />File name does not match the original
                                            </span>
                                        ) : (
                                            <span className="upload-file-item__meta">
                                                {formatBytes(selectedFile.size)} &bull; {getFileTypeName(selectedFile)}
                                            </span>
                                        )}
                                    </div>
                                    <div className="upload-file-item__actions">
                                        <button
                                            type="button"
                                            className="upload-file-item__replace-btn"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            Replace
                                        </button>
                                        <span className="upload-file-item__divider" aria-hidden="true">|</span>
                                        <button
                                            type="button"
                                            className="upload-file-item__remove-btn"
                                            onClick={() => setSelectedFile(null)}
                                            aria-label="Remove file"
                                        >
                                            <TrashCan size={16} />
                                        </button>
                                    </div>
                                </li>
                            </ul>
                            {showMismatch && (
                                <p style={{margin: '0 0 1rem', fontSize: '0.75rem', color: '#da1e28'}}>
                                    Select a file named <strong>{originalFileName}</strong> to resubmit.
                                </p>
                            )}
                            {nameMatches && (
                                <p style={{margin: '0 0 1rem', fontSize: '0.75rem', color: '#198038'}}>
                                    ✓ File name matches — this file will replace the previous version
                                </p>
                            )}
                        </>
                    )}

                    <hr className="upload-section-divider" />

                    <div className="upload-requirements">
                        <div className="upload-requirements__header">
                            <div>
                                <p className="upload-requirements__title">File formatting requirements</p>
                                <p className="upload-requirements__subtitle">
                                    Make sure your files meet the following requirements to avoid errors.
                                </p>
                            </div>
                            <button type="button" className="upload-requirements__download-btn">
                                Download Template <Download size={16} />
                            </button>
                        </div>
                        <table className="upload-requirements__table">
                            <thead>
                                <tr>
                                    <th>Requirement</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {REQUIREMENTS.map(({requirement, details, subRow}) => (
                                    <tr key={requirement} className={subRow ? 'upload-requirements__sub-row' : ''}>
                                        <td>{requirement}</td>
                                        <td>{details}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button type="button" className="upload-requirements__docs-link">
                            View full documentation <Launch size={14} />
                        </button>
                        <div className="upload-tip-box">
                            <InformationFilled size={16} className="upload-tip-box__icon" />
                            <p className="upload-tip-box__text">
                                <strong>Tip:</strong> You can download the template to see the correct format and example data.
                            </p>
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button kind="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button kind="primary" onClick={handleSubmit} disabled={!nameMatches}>
                    Resubmit File
                </Button>
            </ModalFooter>
        </ComposedModal>
    );
};
